import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faFire,
  faMedal,
  faChartLine,
  faPenToSquare,
  faTrashCan,
  faPlus,
  faSave,
  faTimes,
  faSpinner,
  faExclamationTriangle,
  faRedo,
  faCalculator
} from '@fortawesome/free-solid-svg-icons';
import { Role } from '../../components/auth-context/role';
import api from '../../api/api';

export const CareerManager = () => {
  const [activeTab, setActiveTab] = useState('battlepass');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Battle Pass State
  const [currentBattlePass, setCurrentBattlePass] = useState(null);
  const [editingChallenge, setEditingChallenge] = useState(null);
  
  // Milestones State
  const [milestones, setMilestones] = useState([]);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [newMilestone, setNewMilestone] = useState(null);
  
  // Achievements State
  const [achievements, setAchievements] = useState([]);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [newAchievement, setNewAchievement] = useState(null);
  
  // Stats State
  const [careerStats, setCareerStats] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'battlepass':
          const bpResponse = await api.get('/career-admin/battle-pass/current');
          setCurrentBattlePass(bpResponse.data);
          break;
        case 'milestones':
          const milestonesResponse = await api.get('/career-admin/milestones');
          setMilestones(milestonesResponse.data);
          break;
        case 'achievements':
          const achievementsResponse = await api.get('/career-admin/achievements');
          setAchievements(achievementsResponse.data);
          break;
        case 'stats':
          const statsResponse = await api.get('/career-admin/stats');
          setCareerStats(statsResponse.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Battle Pass Functions
  const updateChallenge = async (challengeId) => {
    try {
      const response = await api.put(`/career-admin/battle-pass/challenge/${challengeId}`, editingChallenge);
      setCurrentBattlePass({
        ...currentBattlePass,
        challenges: currentBattlePass.challenges.map(c => 
          c.id === challengeId ? response.data : c
        )
      });
      setEditingChallenge(null);
      alert('Challenge updated successfully');
    } catch (error) {
      alert('Failed to update challenge');
    }
  };

  const deleteChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to delete this challenge?')) return;
    try {
      await api.delete(`/career-admin/battle-pass/challenge/${challengeId}`);
      setCurrentBattlePass({
        ...currentBattlePass,
        challenges: currentBattlePass.challenges.filter(c => c.id !== challengeId)
      });
      alert('Challenge deleted successfully');
    } catch (error) {
      alert('Failed to delete challenge');
    }
  };

  // Milestones Functions
  const createMilestone = async () => {
    try {
      const response = await api.post('/career-admin/milestones', newMilestone);
      setMilestones([...milestones, response.data]);
      setNewMilestone(null);
      alert('Milestone created successfully');
    } catch (error) {
      alert('Failed to create milestone');
    }
  };

  const updateMilestone = async (milestoneId) => {
    try {
      const response = await api.put(`/career-admin/milestones/${milestoneId}`, editingMilestone);
      setMilestones(milestones.map(m => m.id === milestoneId ? response.data : m));
      setEditingMilestone(null);
      alert('Milestone updated successfully');
    } catch (error) {
      alert('Failed to update milestone');
    }
  };

  const deleteMilestone = async (milestoneId) => {
    if (!window.confirm('Are you sure you want to delete this milestone?')) return;
    try {
      await api.delete(`/career-admin/milestones/${milestoneId}`);
      setMilestones(milestones.filter(m => m.id !== milestoneId));
      alert('Milestone deleted successfully');
    } catch (error) {
      alert('Failed to delete milestone');
    }
  };

  // Achievements Functions
  const createAchievement = async () => {
    try {
      const response = await api.post('/career-admin/achievements', newAchievement);
      setAchievements([...achievements, response.data]);
      setNewAchievement(null);
      alert('Achievement created successfully');
    } catch (error) {
      alert('Failed to create achievement');
    }
  };

  const updateAchievement = async (achievementId) => {
    try {
      const response = await api.put(`/career-admin/achievements/${achievementId}`, editingAchievement);
      setAchievements(achievements.map(a => a.id === achievementId ? response.data : a));
      setEditingAchievement(null);
      alert('Achievement updated successfully');
    } catch (error) {
      alert('Failed to update achievement');
    }
  };

  const deleteAchievement = async (achievementId) => {
    if (!window.confirm('Are you sure you want to delete this achievement?')) return;
    try {
      await api.delete(`/career-admin/achievements/${achievementId}`);
      setAchievements(achievements.filter(a => a.id !== achievementId));
      alert('Achievement deleted successfully');
    } catch (error) {
      alert('Failed to delete achievement');
    }
  };

  const resetPilotProgress = async (pilotId) => {
    if (!window.confirm('Are you sure you want to reset all progress for this pilot?')) return;
    try {
      await api.post(`/career-admin/reset-pilot/${pilotId}`);
      alert('Pilot progress reset successfully');
    } catch (error) {
      alert('Failed to reset pilot progress');
    }
  };

  const recalculateAllProgress = async () => {
    if (!window.confirm('This will recalculate hours and progress for ALL pilots. This may take several minutes. Continue?')) return;
    
    try {
      // First recalculate all hours from flights
      const hoursResponse = await api.post('/career-migration/recalculate-all-hours');
      console.log('Hours recalculation:', hoursResponse.data);
      
      // Then recalculate all historical progress
      const progressResponse = await api.post('/career-migration/calculate-historical');
      console.log('Progress recalculation:', progressResponse.data);
      
      alert(`Progress recalculated successfully!\n\nHours: ${hoursResponse.data.updatedPilots}/${hoursResponse.data.totalPilots} pilots updated\nRanks: ${hoursResponse.data.ranksUpdated || 0} pilots promoted\nProgress: ${progressResponse.data.processedPilots}/${progressResponse.data.totalPilots} pilots processed`);
      
      // Reload stats if on stats tab
      if (activeTab === 'stats') {
        loadData();
      }
    } catch (error) {
      console.error('Failed to recalculate progress:', error);
      alert('Failed to recalculate progress. Check console for details.');
    }
  };


  return (
    <div className="view-model">
      <h2>Career Manager</h2>
      <Role has="USER_MANAGER">
        {/* Tab Navigation */}
        <div className="admin-buttons" style={{ marginBottom: '2rem' }}>
          <button 
            className={`btn ${activeTab === 'battlepass' ? 'secondary' : ''}`}
            onClick={() => setActiveTab('battlepass')}
          >
            <FontAwesomeIcon icon={faFire} /> Battle Pass
          </button>
          <button 
            className={`btn ${activeTab === 'milestones' ? 'secondary' : ''}`}
            onClick={() => setActiveTab('milestones')}
          >
            <FontAwesomeIcon icon={faTrophy} /> Milestones
          </button>
          <button 
            className={`btn ${activeTab === 'achievements' ? 'secondary' : ''}`}
            onClick={() => setActiveTab('achievements')}
          >
            <FontAwesomeIcon icon={faMedal} /> Achievements
          </button>
          <button 
            className={`btn ${activeTab === 'stats' ? 'secondary' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <FontAwesomeIcon icon={faChartLine} /> Statistics
          </button>
        </div>

        {error && <p className="error-message">{error}</p>}
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Battle Pass Tab */}
            {activeTab === 'battlepass' && currentBattlePass && (
              <div className="table-container">
                <h3>Current Battle Pass: {currentBattlePass.name || 'No name'}</h3>
                <table className="pilot-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Target</th>
                      <th>Points</th>
                      <th>Icon</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!currentBattlePass.challenges || currentBattlePass.challenges.length === 0) ? (
                      <tr className="background-change">
                        <td colSpan="7">No challenges configured.</td>
                      </tr>
                    ) : (
                      currentBattlePass.challenges.map((challenge) => (
                        <tr key={challenge.id} className="background-change">
                          {editingChallenge?.id === challenge.id ? (
                            <>
                              <td>{challenge.type}</td>
                              <td>
                                <input
                                  type="text"
                                  value={editingChallenge.name}
                                  onChange={(e) => setEditingChallenge({...editingChallenge, name: e.target.value})}
                                  style={{ width: '100%' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editingChallenge.description}
                                  onChange={(e) => setEditingChallenge({...editingChallenge, description: e.target.value})}
                                  style={{ width: '100%' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={editingChallenge.targetValue}
                                  onChange={(e) => setEditingChallenge({...editingChallenge, targetValue: parseInt(e.target.value)})}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={editingChallenge.points}
                                  onChange={(e) => setEditingChallenge({...editingChallenge, points: parseInt(e.target.value)})}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editingChallenge.icon || ''}
                                  onChange={(e) => setEditingChallenge({...editingChallenge, icon: e.target.value})}
                                  style={{ width: '100%' }}
                                />
                              </td>
                              <td>
                                <button className="btn" onClick={() => updateChallenge(challenge.id)}>
                                  <FontAwesomeIcon icon={faSave} /> Save
                                </button>
                                <button className="btn" onClick={() => setEditingChallenge(null)}>
                                  <FontAwesomeIcon icon={faTimes} /> Cancel
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{challenge.type}</td>
                              <td>{challenge.name}</td>
                              <td>{challenge.description}</td>
                              <td>{challenge.targetValue}</td>
                              <td>{challenge.points}</td>
                              <td>{challenge.icon || 'N/A'}</td>
                              <td>
                                <button 
                                  className="btn"
                                  onClick={() => setEditingChallenge(challenge)}
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} /> Edit
                                </button>
                                <button 
                                  className="btn"
                                  onClick={() => deleteChallenge(challenge.id)}
                                >
                                  <FontAwesomeIcon icon={faTrashCan} /> Delete
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Milestones Tab */}
            {activeTab === 'milestones' && (
              <div className="table-container">
                <div style={{ marginBottom: '1rem' }}>
                  <button 
                    className="btn primary"
                    onClick={() => setNewMilestone({
                      type: 'HOURS',
                      name: '',
                      description: '',
                      requiredHours: 0,
                      requiredFlights: 0,
                      points: 100,
                      level: 1
                    })}
                  >
                    <FontAwesomeIcon icon={faPlus} /> New Milestone
                  </button>
                </div>

                {newMilestone && (
                  <div style={{ 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    border: '2px solid var(--accent-primary)',
                    borderRadius: '0.5rem',
                    background: 'var(--background-secondary)'
                  }}>
                    <h4>Create New Milestone</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                      <select 
                        value={newMilestone.type}
                        onChange={(e) => setNewMilestone({...newMilestone, type: e.target.value})}
                      >
                        <option value="HOURS">Hours</option>
                        <option value="FLIGHTS">Flights</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Name"
                        value={newMilestone.name}
                        onChange={(e) => setNewMilestone({...newMilestone, name: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={newMilestone.description}
                        onChange={(e) => setNewMilestone({...newMilestone, description: e.target.value})}
                      />
                      <input
                        type="number"
                        placeholder={newMilestone.type === 'HOURS' ? 'Required hours' : 'Required flights'}
                        value={newMilestone.type === 'HOURS' ? newMilestone.requiredHours : newMilestone.requiredFlights}
                        onChange={(e) => setNewMilestone({
                          ...newMilestone,
                          [newMilestone.type === 'HOURS' ? 'requiredHours' : 'requiredFlights']: parseInt(e.target.value)
                        })}
                      />
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <button className="btn primary" onClick={createMilestone}>
                        <FontAwesomeIcon icon={faSave} /> Create
                      </button>
                      <button className="btn" onClick={() => setNewMilestone(null)}>
                        <FontAwesomeIcon icon={faTimes} /> Cancel
                      </button>
                    </div>
                  </div>
                )}

                <table className="pilot-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Requirement</th>
                      <th>Points</th>
                      <th>Level</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {milestones.length === 0 ? (
                      <tr className="background-change">
                        <td colSpan="7">No milestones configured.</td>
                      </tr>
                    ) : (
                      milestones.map((milestone) => (
                        <tr key={milestone.id} className="background-change">
                          {editingMilestone?.id === milestone.id ? (
                            <>
                              <td>{milestone.type}</td>
                              <td>
                                <input
                                  type="text"
                                  value={editingMilestone.name}
                                  onChange={(e) => setEditingMilestone({...editingMilestone, name: e.target.value})}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editingMilestone.description}
                                  onChange={(e) => setEditingMilestone({...editingMilestone, description: e.target.value})}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={milestone.type === 'HOURS' ? editingMilestone.requiredHours : editingMilestone.requiredFlights}
                                  onChange={(e) => setEditingMilestone({
                                    ...editingMilestone,
                                    [milestone.type === 'HOURS' ? 'requiredHours' : 'requiredFlights']: parseInt(e.target.value)
                                  })}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={editingMilestone.points}
                                  onChange={(e) => setEditingMilestone({...editingMilestone, points: parseInt(e.target.value)})}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={editingMilestone.level}
                                  onChange={(e) => setEditingMilestone({...editingMilestone, level: parseInt(e.target.value)})}
                                  style={{ width: '60px' }}
                                />
                              </td>
                              <td>
                                <button className="btn" onClick={() => updateMilestone(milestone.id)}>
                                  <FontAwesomeIcon icon={faSave} />
                                </button>
                                <button className="btn" onClick={() => setEditingMilestone(null)}>
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{milestone.type}</td>
                              <td>{milestone.name}</td>
                              <td>{milestone.description}</td>
                              <td>
                                {milestone.type === 'HOURS' 
                                  ? `${milestone.requiredHours}h`
                                  : `${milestone.requiredFlights} flights`}
                              </td>
                              <td>{milestone.points}</td>
                              <td>{milestone.level}</td>
                              <td>
                                <button className="btn" onClick={() => setEditingMilestone(milestone)}>
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                                <button className="btn" onClick={() => deleteMilestone(milestone.id)}>
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <div className="table-container">
                <div style={{ marginBottom: '1rem' }}>
                  <button 
                    className="btn primary"
                    onClick={() => setNewAchievement({
                      category: 'SPECIAL',
                      name: '',
                      description: '',
                      rarity: 'common',
                      points: 50,
                      isSecret: false,
                      icon: ''
                    })}
                  >
                    <FontAwesomeIcon icon={faPlus} /> New Achievement
                  </button>
                </div>

                {newAchievement && (
                  <div style={{ 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    border: '2px solid var(--accent-primary)',
                    borderRadius: '0.5rem',
                    background: 'var(--background-secondary)'
                  }}>
                    <h4>Create New Achievement</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                      <select 
                        value={newAchievement.category}
                        onChange={(e) => setNewAchievement({...newAchievement, category: e.target.value})}
                      >
                        <option value="SPECIAL">Special</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="LANDING">Landing</option>
                        <option value="EXPLORATION">Exploration</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Name"
                        value={newAchievement.name}
                        onChange={(e) => setNewAchievement({...newAchievement, name: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={newAchievement.description}
                        onChange={(e) => setNewAchievement({...newAchievement, description: e.target.value})}
                      />
                      <select 
                        value={newAchievement.rarity}
                        onChange={(e) => setNewAchievement({...newAchievement, rarity: e.target.value})}
                      >
                        <option value="common">Common</option>
                        <option value="rare">Rare</option>
                        <option value="epic">Epic</option>
                        <option value="legendary">Legendary</option>
                      </select>
                      <input
                        type="number"
                        placeholder="Points"
                        value={newAchievement.points}
                        onChange={(e) => setNewAchievement({...newAchievement, points: parseInt(e.target.value)})}
                      />
                      <label>
                        <input
                          type="checkbox"
                          checked={newAchievement.isSecret}
                          onChange={(e) => setNewAchievement({...newAchievement, isSecret: e.target.checked})}
                        />
                        Secret
                      </label>
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <button className="btn primary" onClick={createAchievement}>
                        <FontAwesomeIcon icon={faSave} /> Create
                      </button>
                      <button className="btn" onClick={() => setNewAchievement(null)}>
                        <FontAwesomeIcon icon={faTimes} /> Cancel
                      </button>
                    </div>
                  </div>
                )}

                <table className="pilot-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Rarity</th>
                      <th>Points</th>
                      <th>Secret</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {achievements.length === 0 ? (
                      <tr className="background-change">
                        <td colSpan="7">No achievements configured.</td>
                      </tr>
                    ) : (
                      achievements.map((achievement) => (
                        <tr key={achievement.id} className="background-change">
                          {editingAchievement?.id === achievement.id ? (
                            <>
                              <td>
                                <select 
                                  value={editingAchievement.category}
                                  onChange={(e) => setEditingAchievement({...editingAchievement, category: e.target.value})}
                                >
                                  <option value="SPECIAL">Special</option>
                                  <option value="MONTHLY">Monthly</option>
                                  <option value="LANDING">Landing</option>
                                  <option value="EXPLORATION">Exploration</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editingAchievement.name}
                                  onChange={(e) => setEditingAchievement({...editingAchievement, name: e.target.value})}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editingAchievement.description}
                                  onChange={(e) => setEditingAchievement({...editingAchievement, description: e.target.value})}
                                />
                              </td>
                              <td>
                                <select 
                                  value={editingAchievement.rarity}
                                  onChange={(e) => setEditingAchievement({...editingAchievement, rarity: e.target.value})}
                                >
                                  <option value="common">Common</option>
                                  <option value="rare">Rare</option>
                                  <option value="epic">Epic</option>
                                  <option value="legendary">Legendary</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={editingAchievement.points}
                                  onChange={(e) => setEditingAchievement({...editingAchievement, points: parseInt(e.target.value)})}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={editingAchievement.isSecret}
                                  onChange={(e) => setEditingAchievement({...editingAchievement, isSecret: e.target.checked})}
                                />
                              </td>
                              <td>
                                <button className="btn" onClick={() => updateAchievement(achievement.id)}>
                                  <FontAwesomeIcon icon={faSave} />
                                </button>
                                <button className="btn" onClick={() => setEditingAchievement(null)}>
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{achievement.category}</td>
                              <td>{achievement.name}</td>
                              <td>{achievement.description}</td>
                              <td>{achievement.rarity}</td>
                              <td>{achievement.points}</td>
                              <td>{achievement.isSecret ? '✔' : '✘'}</td>
                              <td>
                                <button className="btn" onClick={() => setEditingAchievement(achievement)}>
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                                <button className="btn" onClick={() => deleteAchievement(achievement.id)}>
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Statistics Tab */}
            {activeTab === 'stats' && careerStats && (
              <div>
                <div className="table-container">
                  <h3>Career System Statistics</h3>
                  <table className="pilot-table">
                    <tbody>
                      <tr className="background-change">
                        <td><strong>Total Pilots</strong></td>
                        <td>{careerStats.totalPilots}</td>
                      </tr>
                      <tr className="background-change">
                        <td><strong>Active Pilots (last 30 days)</strong></td>
                        <td>{careerStats.activePilots}</td>
                      </tr>
                      <tr className="background-change">
                        <td><strong>Total Milestones</strong></td>
                        <td>{careerStats.totalMilestones}</td>
                      </tr>
                      <tr className="background-change">
                        <td><strong>Total Achievements</strong></td>
                        <td>{careerStats.totalAchievements}</td>
                      </tr>
                      <tr className="background-change">
                        <td><strong>Average Points</strong></td>
                        <td>{careerStats.averagePoints}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="table-container" style={{ marginTop: '2rem' }}>
                  <h3>
                    <FontAwesomeIcon icon={faCalculator} /> Maintenance Actions
                  </h3>
                  <p>Use these actions to maintain and recalculate pilot progress data.</p>
                  
                  <div style={{ marginTop: '1rem' }}>
                    <button 
                      className="btn primary"
                      onClick={recalculateAllProgress}
                    >
                      <FontAwesomeIcon icon={faCalculator} /> Recalculate All Pilots Progress
                    </button>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                      This will recalculate hours from flights and update all career progress for all pilots.
                    </p>
                  </div>
                </div>

                <div className="table-container" style={{ marginTop: '2rem' }}>
                  <h3>
                    <FontAwesomeIcon icon={faExclamationTriangle} /> Danger Zone
                  </h3>
                  <p>These actions are irreversible and will affect pilot progress.</p>
                  
                  <div style={{ marginTop: '1rem' }}>
                    <input 
                      type="number" 
                      placeholder="Pilot ID"
                      id="pilotIdReset"
                      style={{ marginRight: '1rem' }}
                    />
                    <button 
                      className="btn"
                      style={{ background: '#dc2626', color: 'white' }}
                      onClick={() => {
                        const pilotId = document.getElementById('pilotIdReset').value;
                        if (pilotId) resetPilotProgress(pilotId);
                      }}
                    >
                      <FontAwesomeIcon icon={faRedo} /> Reset Progress
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Role>
    </div>
  );
};

export default CareerManager;